import { Injectable, signal, computed, Signal } from '@angular/core';

/**
 * Environment configuration interface
 */
export interface EnvConfig {
  version: string;
  enabled: boolean;
  clientValidationEnabled: boolean;
  unobtrusiveJavaScriptEnabled: boolean;
  redirectLogout: string;
  redirectUri: string;
  apiUrl: string;
  apiStorageUrl: string;
  businessEndpoint: string;
  appName: string;
  [key: string]: any; // Allow additional properties for extensibility
}

/**
 * Environment Service
 * Loads and provides access to environment configuration for all apps
 */
@Injectable({ providedIn: 'root' })
export class EnvService {
  // Signal để lưu config - reactive và có thể subscribe
  // Khởi tạo với null, chỉ set sau khi load xong
  private readonly configSignal = signal<EnvConfig | null>(null);
  
  // Signal để track trạng thái load
  private readonly isLoadedSignal = signal<boolean>(false);
  
  // Computed signal cho API URL - tự động update khi config thay đổi
  readonly apiUrl = computed(() => this.configSignal()?.apiUrl || '');
  
  // Computed signal cho API Storage URL
  readonly apiStorageUrl = computed(() => this.configSignal()?.apiStorageUrl || '');
  
  // Expose config as readonly signal
  readonly config: Signal<EnvConfig | null> = this.configSignal.asReadonly();
  
  // Expose isLoaded as readonly signal
  readonly isLoaded: Signal<boolean> = this.isLoadedSignal.asReadonly();

  constructor() {
    // EnvService initialized
  }

  /**
   * Load environment configuration from env.json
   */
  async load(): Promise<void> {
    try {
      const res = await fetch('/assets/env/env.json');
      if (!res.ok) {
        throw new Error(`Failed to load env.json: ${res.status} ${res.statusText}`);
      }
      
      // Read response text first
      const responseText = await res.text();
      
      // Parse JSON from response text
      const configData = JSON.parse(responseText) as EnvConfig;
      
      // Update signal - sẽ trigger tất cả computed signals và subscribers
      this.configSignal.set(configData);
      this.isLoadedSignal.set(true);
    } catch (error) {
      // Fallback to default config nếu load thất bại
      console.warn('⚠️ Failed to load env.json, using default config');
      const defaultConfig = this.getDefaultConfig();
      this.configSignal.set(defaultConfig);
      this.isLoadedSignal.set(true);
    }
  }

  /**
   * Get the entire configuration object
   * @returns Current config value or default if not loaded
   */
  getConfig(): EnvConfig {
    const config = this.configSignal();
    if (!config) {
      console.warn('⚠️ EnvService.getConfig() called before load completed, using default');
      return this.getDefaultConfig();
    }
    return config;
  }

  /**
   * Get a specific config value by key
   * @param key Config key to retrieve
   * @returns Config value for the specified key
   */
  get<T = any>(key: keyof EnvConfig | string): T {
    const config = this.configSignal();
    if (!config) {
      console.warn(`⚠️ EnvService.get('${String(key)}') called before load completed, using default`);
      return this.getDefaultConfig()[key] as T;
    }
    return config[key] as T;
  }

  /**
   * Get server API URL
   * @returns API URL from config
   */
  getApiUrl(): string {
    const config = this.configSignal();
    if (!config) {
      console.warn('⚠️ EnvService.getApiUrl() called before config loaded, using default');
      return this.getDefaultConfig().apiUrl;
    }
    return config.apiUrl || '';
  }
  
  /**
   * Get server API Storage URL
   * @returns API Storage URL from config
   */
  getApiStorageUrl(): string {
    const config = this.configSignal();
    if (!config) {
      console.warn('⚠️ EnvService.getApiStorageUrl() called before load completed, using default');
      return this.getDefaultConfig().apiStorageUrl;
    }
    return config.apiStorageUrl || '';
  }

  /**
   * Get default configuration (fallback)
   * Sử dụng khi không load được env.json
   */
  private getDefaultConfig(): EnvConfig {
    return {
      version: '1.0.0',
      enabled: false,
      clientValidationEnabled: true,
      unobtrusiveJavaScriptEnabled: true,
      apiUrl: 'https://apinanouhp.quanlyphongkham.net',
      apiStorageUrl: 'https://apistorage.quanlyphongkham.net',
      businessEndpoint: '/api/business/private',
      appName: 'HIS Web Application',
      redirectLogout: 'https://local.web.msi.com/Login/Logout/',
      redirectUri: 'https://local.web.msi.com/Login/SigninOffice365CallBack/',
    };
  }
}
